import React from "react";
import { createPortal } from "react-dom";
import { ChevronDownIcon } from "../../atoms/ChevronDownIcon";
import { usePortalMenu } from "../../../hooks/usePortalMenu";
import { PUBLISH_STATUS_LABEL } from "../StoryHeaderActions/StoryHeaderActions.constants";
import { NavMenuItem, NavMenuList, NavMenuPortal } from "../StoryHeaderActions/StoryHeaderActions.styles";
import {
  StatusChipButton,
  Container,
  HomeCrumb,
  HomeText,
  Separator,
  Title,
  TitleButton,
  StatusPill,
} from "./StoryHeaderTitle.styles";
import { TitleInput } from "../StoryCanvasHeader/StoryCanvasHeader.styles";
import type { StoryHeaderTitleProps } from "./StoryHeaderTitle.types";
import { useStoryHeaderTitleInternalState } from "./useStoryHeaderTitleInternalState";

/**
 * Story-detail header breadcrumb (F05): "Data stories / title" plus the status chip (the publish menu trigger).
 * Stateless molecule — the story values are supplied by the page via StoryHeaderContext.
 */
export const StoryHeaderTitle: React.FC<StoryHeaderTitleProps> = ({
  storyTitle,
  onTitleSave,
  homeLabel = "Data stories",
  onHomeClick,
  statusLabel,
  publishStatus,
  statusDropdownOpen = false,
  onStatusDropdownToggle,
  statusOptions,
  onStatusSelect,
}) => {
  const edit = useStoryHeaderTitleInternalState(storyTitle, onTitleSave);
  const { triggerRef, menuRef, rect } = usePortalMenu<HTMLButtonElement, HTMLUListElement>({
    open: statusDropdownOpen,
    onOutsideClick: onStatusDropdownToggle,
  });
  return (
    <Container>
      {onHomeClick ? (
        <HomeCrumb type="button" onClick={onHomeClick}>{homeLabel}</HomeCrumb>
      ) : (
        <HomeText>{homeLabel}</HomeText>
      )}
      <Separator aria-hidden>/</Separator>
      {edit.isEditing ? (
        <TitleInput
          value={edit.draft}
          autoFocus
          aria-label="Story title"
          onChange={(e) => edit.setDraft(e.target.value)}
          onBlur={edit.commit}
          onKeyDown={(e) => {
            if (e.key === "Enter") edit.commit();
            if (e.key === "Escape") edit.cancel();
          }}
        />
      ) : onTitleSave ? (
        <TitleButton type="button" title={storyTitle} aria-label="Rename story" onClick={edit.start}>
          {storyTitle}
        </TitleButton>
      ) : (
        <Title>{storyTitle}</Title>
      )}
      {publishStatus ? (
        <StatusChipButton
          ref={triggerRef}
          type="button"
          onClick={onStatusDropdownToggle}
          aria-haspopup="listbox"
          aria-expanded={statusDropdownOpen}
          aria-label={`Story status: ${PUBLISH_STATUS_LABEL[publishStatus]}`}
        >
          {PUBLISH_STATUS_LABEL[publishStatus]}
          <ChevronDownIcon size={12} color="currentColor" />
        </StatusChipButton>
      ) : (
        statusLabel && <StatusPill>{statusLabel}</StatusPill>
      )}
      {statusDropdownOpen &&
        statusOptions &&
        rect &&
        createPortal(
          <NavMenuPortal
            ref={menuRef}
            role="listbox"
            style={{ top: rect.top, bottom: rect.bottom, left: rect.left }}
          >
            {statusOptions.map((opt) => (
              <NavMenuList key={opt.value}>
                <NavMenuItem
                  role="option"
                  aria-selected={opt.value === publishStatus}
                  onClick={() => {
                    onStatusSelect?.(opt.value);
                    onStatusDropdownToggle?.();
                  }}
                >
                  {opt.label}
                </NavMenuItem>
              </NavMenuList>
            ))}
          </NavMenuPortal>,
          document.body,
        )}
    </Container>
  );
};

export default StoryHeaderTitle;
